import { Routes } from "@angular/router";
import { MainLayoutComponent } from "./core/layout/main-layout/main-layout.component";
import { superRoutes } from "./features/super/super.routes";
import { bibleModalRoutes, bibleRoutes } from "./features/bible/bible.routes";
import { classesRoutes } from "./features/classes/classes.routes";
import { classroomRoutes } from "./features/classroom/classroom.routes";
import { LEARN_ROUTES } from "./features/learn/learn.routes";
import { plannerRoutes } from "./features/planner/planner.routes";
import { adminRoutes } from "./features/admin/admin.routes";
import {
  standardsModalRoutes,
  standardsRoutes,
} from "./features/standards/standards.routes";
import { studentRoutes } from "./features/student/student.routes";
import { superGuard } from "./subscription/guards/super.guard";
import { authenticatedGuard } from "./subscription/guards/authenticated.guard";
import { onboardedGuard } from "./subscription/guards/onboarded.guard";
import { orgPendingGuard } from "./subscription/guards/org-pending.guard";
import { setupGuard } from "./subscription/guards/setup.guard";
import { subscribedGuard } from "./subscription/guards/subscribed.guard";
import { subscriptionRoutes } from "./subscription/subscription.routes";
import { legacyOrgPathRoutes, publicSiteRoutes, shareRoutes } from "./features/share/share.routes";

/**
 * Universal routes — work the same in both trees (app side and org public side).
 * These are public, anonymous-friendly pages whose meaning doesn't change based
 * on org context.
 */
const universalRoutes: Routes = [
  // Public share artifacts: /s/doc/:id, /s/planner/:id, etc.
  ...shareRoutes,

  // Legacy `/s/:siteHandle/...` redirect — must come AFTER shareRoutes so
  // specific share artifact paths win. Redirects to the org's subdomain in
  // prod, or to `?org=siteHandle` on localhost in dev.
  ...legacyOrgPathRoutes,

  // Public teacher classroom pages: /u/:teacherHandle and nested. The
  // shell fetches the teacher once and renders the hero + chrome; child
  // routes consume the teacher via TeacherClassroomContext.
  {
    path: "u/:teacherHandle",
    loadComponent: () =>
      import("./features/share/classroom-shell/classroom-shell.component").then(
        (m) => m.ClassroomShellComponent,
      ),
    children: [
      // Classroom landing: /u/:teacher — bio, classes, planners, support
      {
        path: "",
        loadComponent: () =>
          import("./features/share/classroom-landing/classroom-landing.component").then(
            (m) => m.ClassroomLandingComponent,
          ),
      },
      // Single planner inside the classroom: /u/:teacher/planner/:plannerId
      // No /u/:teacher/plans index page — the classroom landing lists
      // public planners via a block.
      {
        path: "planner/:plannerId",
        loadComponent: () =>
          import(
            "./features/share/planner-detail-page/planner-detail-page.component"
          ).then((m) => m.PlannerDetailPageComponent),
      },
      // Single class inside the classroom: /u/:teacher/class/:classId — the
      // segment matches the in-org shape (/o/{org}/u/:userId/class/:classId)
      // so the same path works regardless of org context.
      {
        path: "class/:classId",
        loadComponent: () =>
          import(
            "./features/share/campus-class-page/campus-class-page.component"
          ).then((m) => m.CampusClassPageComponent),
      },
    ],
  },

  // Email/marketing landing pages
  {
    path: "learn",
    children: LEARN_ROUTES,
  },
  {
    path: "unsubscribe",
    loadComponent: () =>
      import("./features/unsubscribe/unsubscribe-page.component").then(
        (m) => m.UnsubscribePageComponent,
      ),
  },
  {
    path: "join/:token",
    loadComponent: () =>
      import("./subscription/pages/join/join.component").then(
        (m) => m.JoinPageComponent,
      ),
  },

  // Modal-style routes used by both trees
  ...standardsModalRoutes,
  ...bibleModalRoutes,
];

/**
 * App routes — authenticated workspace. Lives on `app.classjax.com` in prod
 * and `localhost` (without `?org=`) in dev.
 */
const appRoutes: Routes = [
  ...universalRoutes,

  // Authenticated app shell — everything under MainLayoutComponent requires login.
  {
    path: "",
    component: MainLayoutComponent,
    canActivate: [authenticatedGuard, orgPendingGuard, setupGuard, subscribedGuard, onboardedGuard],
    runGuardsAndResolvers: "always",
    children: [
      {
        path: "",
        redirectTo: "classroom",
        pathMatch: "full",
      },
      {
        path: "dashboard",
        redirectTo: "classroom",
        pathMatch: "full",
      },
      {
        path: "documents",
        loadComponent: () =>
          import("./features/documents/documents-page/documents-page.component")
            .then(
              (m) => m.DocumentsPageComponent,
            ),
      },
      {
        path: "documents/:documentId",
        loadComponent: () =>
          import("./features/documents/documents-page/documents-page.component")
            .then(
              (m) => m.DocumentsPageComponent,
            ),
      },
      {
        path: "artifacts",
        loadComponent: () =>
          import("./features/artifacts/artifacts-page.component").then(
            (m) => m.ArtifactsPageComponent,
          ),
      },
      {
        path: "usage",
        loadComponent: () =>
          import("./features/usage/usage-page/usage-page.component").then(
            (m) => m.UsagePageComponent,
          ),
      },
      {
        path: "document/:documentId",
        loadComponent: () =>
          import("./features/documents/document-page/document-page.component")
            .then(
              (m) => m.DocumentPageComponent,
            ),
      },
      {
        path: "document/:documentId/entries",
        loadComponent: () =>
          import("./features/documents/form-entries-page/form-entries-page.component")
            .then(
              (m) => m.FormEntriesPageComponent,
            ),
      },
      {
        path: "profile",
        redirectTo: "classroom",
        pathMatch: "full",
      },
      {
        path: "settings",
        loadComponent: () =>
          import("./features/settings/settings-page.component").then(
            (m) => m.SettingsPageComponent,
          ),
      },
      {
        path: "safety",
        loadComponent: () =>
          import("./features/safety/safety-page.component").then(
            (m) => m.SafetyPageComponent,
          ),
      },
      ...classroomRoutes,
      ...classesRoutes,
      ...plannerRoutes,
      ...standardsRoutes,
      ...bibleRoutes,
      ...adminRoutes,
      // /super — Classjax-internal staff section. Gated by superGuard (user.is_super).
      ...superRoutes.map((r) => ({
        ...r,
        canActivate: [...(r.canActivate ?? []), superGuard],
      })),
      // Right-side panel routes (jax outlet) — Jax assistant or Help.
      // Mutually exclusive: opening one replaces the other.
      {
        path: "assistant",
        outlet: "jax",
        loadComponent: () =>
          import("./core/jax/jax-offcanvas-content/jax-offcanvas-content.component")
            .then((m) => m.JaxOffcanvasContentComponent),
      },
      {
        path: "help",
        outlet: "jax",
        loadComponent: () =>
          import("./features/help/help-offcanvas/help-offcanvas.component")
            .then((m) => m.HelpOffcanvasComponent),
      },
      {
        path: "help/:slug",
        outlet: "jax",
        loadComponent: () =>
          import("./features/help/help-offcanvas/help-offcanvas.component")
            .then((m) => m.HelpOffcanvasComponent),
      },
    ],
  },

  // Student routes (separate layout, no subscription required)
  ...studentRoutes,
  // Subscription module routes (signin, signup, forgot-password, etc.)
  ...subscriptionRoutes,

  // Wildcard - show 404 for any unmatched route
  {
    path: "**",
    loadComponent: () =>
      import("./features/not-found/not-found-page.component").then(
        (m) => m.NotFoundPageComponent,
      ),
  },
];

/**
 * Org public-site routes — read-only public website for visitors.
 *
 * Mounted in two ways depending on context:
 *
 * - **Subdomain mode** (prod): `acme.classjax.com` → routes live at `/`.
 *   The tree below is spread directly into the root route list.
 *
 * - **Path-prefix mode** (localhost, no subdomain): URLs look like
 *   `/o/{orgHandle}/...`. The tree below is nested under an
 *   `/o/:orgHandle` parent route.
 *
 * No auth UI, no session reads. See migration plan: "Architecture: two-tree routing".
 */
const orgSiteInnerRoutes: Routes = [
  // The org's public pages — home, custom pages, teacher pages within org,
  // campus pages, etc. We DO NOT spread `universalRoutes` here: inside an
  // org, the org-aware teacher route in publicSiteRoutes must win over the
  // bare /u/:handle in universalRoutes (otherwise the unbranded classroom
  // shell would shadow it). Universal routes still mount at the root of
  // the path-prefix tree for non-org URLs.
  ...publicSiteRoutes,

  // Wildcard - show 404 for any unmatched route
  {
    path: "**",
    loadComponent: () =>
      import("./features/not-found/not-found-page.component").then(
        (m) => m.NotFoundPageComponent,
      ),
  },
];

/** Subdomain-mode tree: routes live at `/`. */
const subdomainOrgSiteRoutes: Routes = orgSiteInnerRoutes;

/** Path-prefix-mode tree: routes live under `/o/:orgHandle/...`. */
const pathPrefixOrgSiteRoutes: Routes = [
  // Universal/shared routes still need to work without an org prefix
  // (e.g. /s/doc/:id share artifacts, /join/:token, /learn, /u/:teacherHandle).
  ...universalRoutes,

  {
    path: "o/:orgHandle",
    children: orgSiteInnerRoutes,
  },

  // Wildcard catches anything not matched above.
  {
    path: "**",
    loadComponent: () =>
      import("./features/not-found/not-found-page.component").then(
        (m) => m.NotFoundPageComponent,
      ),
  },
];

const RESERVED_SUBDOMAINS = ["app", "www", "api", "search", "ai"];

/**
 * Decide which route tree to use based on the current host + path.
 * Runs once at module-load time, before Angular builds the router.
 *
 * Detection (in priority order):
 * 1. URL path starts with `/o/{handle}` → path-prefix org mode. Works on
 *    ANY host — including subdomains and `app.` — so org links of the
 *    `/o/{handle}/...` form are universally shareable. Lets an admin
 *    preview another org's public site without leaving their session.
 * 2. Otherwise, subdomain on `*.classjax.com` (non-reserved) → subdomain
 *    org mode (org tree mounted at `/`).
 * 3. Everything else → app mode (the authenticated workspace tree).
 *
 * No localStorage. URL is the source of truth.
 */
type OrgMode = "subdomain" | "pathPrefix" | "app";

function detectMode(): OrgMode {
  if (typeof window === "undefined") {
    return "app";
  }

  // Path-prefix wins everywhere: same URL pattern works on localhost, on
  // app.classjax.com, on a customer subdomain, etc.
  if (/^\/o\/[^/?]+/.test(window.location.pathname)) {
    return "pathPrefix";
  }

  const host = window.location.hostname || "";

  // Prod: subdomain on *.classjax.com that isn't reserved → org tree at /.
  if (host.endsWith(".classjax.com")) {
    const subdomain = host.replace(".classjax.com", "");
    if (!RESERVED_SUBDOMAINS.includes(subdomain)) {
      return "subdomain";
    }
  }

  return "app";
}

const mode = detectMode();
export const routes: Routes =
  mode === "subdomain"
    ? subdomainOrgSiteRoutes
    : mode === "pathPrefix"
      ? pathPrefixOrgSiteRoutes
      : appRoutes;
